import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useLocation } from "react-router-dom";
dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const dateFormat = "MMM DD ,YYYY";

const VisitorContractDetails = ({ onSuccess, setFlowStep }) => {
  const location = useLocation();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [questData, setGuestData] = useState();
  const queryParams = new URLSearchParams(location.search);
  const Verification_token = queryParams.get("token");
  const PUBLIC_API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const initialValues = {
    name: "",
    surname: "",
    phone: "",
    email: "",
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().add(1, "week").format("YYYY-MM-DD"),
    cleanliness: "",
    slider_score: 30,
    role: "guest",
    rules: {
      petsAllowed: true,
      sharedSpace: true,
      smokingAllowed: true,
    },
    expectations: {
      trash: false,
      guests: false,
    },
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    surname: Yup.string().required("Surname is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string().required("Phone is required"),
    start_date: Yup.string().required("Start date is required"),
    end_date: Yup.string()
      .required("End date is required")
      .test(
        "is-after-start",
        "End date must be after start date",
        function (endDate) {
          const { start_date } = this.parent;
          return (
            !start_date || !endDate || dayjs(endDate).isAfter(dayjs(start_date))
          );
        }
      ),
    rules: Yup.object().shape({
      petsAllowed: Yup.boolean().required(),
      smokingAllowed: Yup.boolean().required(),
      sharedSpace: Yup.boolean().required(),
    }),
    cleanliness: Yup.string().required("Cleanliness is required"),
    slider_score: Yup.number()
      .min(1, "Minimum rating is 1")
      .max(100, "Maximum rating is 100")
      .required("Rating is required"),
  });

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const requestData = {
        start_date: values.start_date,
        end_date: values.end_date,
        role: "guest",
        name: values.name,
        surname: values.surname,
        email: values.email,
        phone: values.phone,
        cleanliness: values.cleanliness,
        slider_score: values.slider_score,
        rules: values.rules,
        expectations: values.expectations,
      };
      if (Verification_token)
        requestData.verification_token = Verification_token;
      setIsLoading(true);
      const response = await axios.post(
        `${PUBLIC_API_URL}contract/submit`,
        requestData
      );
      setGuestData(response.data.data);
      if (
        response.data &&
        response.data.data &&
        response.data.data.contract &&
        response.data.data.contract.verification_token
      ) {
        onSuccess(
          response.data.data,
          response.data.data.contract.verification_token
        );
      } else {
        console.warn("Missing contract or verification_token in response");
        onSuccess(response.data.data, "");
      }

      setSubmitted(true);
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setSubmitting(false);
      setIsLoading(false);
      Verification_token ? setFlowStep("pdf") : setFlowStep("shareContract");
    }
  };

  const [apiDates, setApiDates] = useState(null);

  useEffect(() => {
    if (!Verification_token) return;

    const fetchDates = async () => {
      try {
        const response = await axios.get(
          `${PUBLIC_API_URL}contract/?token=${Verification_token}`
        );
        const { start_date, end_date } = response.data.data;
        if (start_date && end_date) {
          setApiDates({
            start_date: dayjs(start_date).format("YYYY-MM-DD"),
            end_date: dayjs(end_date).format("YYYY-MM-DD"),
          });
        }
      } catch (error) {
        console.error("Error fetching dates:", error);
      }
    };

    fetchDates();
  }, [Verification_token, PUBLIC_API_URL]);

  const PhoneNumberInput = ({ field, form, ...props }) => {
    return (
      <div className="custom-phone-input-container">
        <PhoneInput
          country={"gb"}
          value={field.value}
          onChange={(value) => form.setFieldValue(field.name, value)}
          inputClass="custom-phone-input"
          containerClass="custom-phone-container"
          buttonClass="custom-flag-button"
          dropdownClass="custom-phone-dropdown"
          inputProps={{
            name: field.name,
            id: field.name,
            placeholder: "Phone number",
            required: true,
          }}
        />
        <ErrorMessage
          name={field.name}
          component="div"
          className="error-message"
        />
      </div>
    );
  };
  return (
    <div>
      <div className="contract_form_container">
        <div className="contract_form">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            validateOnBlur={true}
            validateOnChange={false}
          >
            {(formikProps) => (
              <Form>
                <div className="contract_date_field">
                  <label
                    htmlFor="title"
                    className="contract_field_title"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    2. What are the dates of the swap?
                  </label>
                  <div className="contract_date_picker_wrapper">
                    <label htmlFor="date" id="date_picker_label">
                      {screenWidth <= 768 ? "Select the Date" : ""}
                    </label>

                    <Field name="date_range">
                      {({ field, form }) => (
                        <>
                          <RangePicker
                            format={dateFormat}
                            onChange={(dates) => {
                              if (dates && dates[0] && dates[1]) {
                                form.setFieldValue(
                                  "start_date",
                                  dates[0].format("YYYY-MM-DD")
                                );
                                form.setFieldValue(
                                  "end_date",
                                  dates[1].format("YYYY-MM-DD")
                                );
                              }
                            }}
                            value={[
                              apiDates?.start_date
                                ? dayjs(apiDates.start_date)
                                : form.values.start_date
                                ? dayjs(form.values.start_date)
                                : null,
                              apiDates?.end_date
                                ? dayjs(apiDates.end_date)
                                : form.values.end_date
                                ? dayjs(form.values.end_date)
                                : null,
                            ]}
                            className="contract_date_picker"
                            classNames={{
                              popup: {
                                root: "custom_range_picker_dropdown",
                              },
                            }}
                            getPopupContainer={(trigger) =>
                              trigger.parentElement || document.body
                            }
                            disabled={!!Verification_token}
                          />
                          <span className="dates_error_msg">
                            <ErrorMessage
                              name="start_date"
                              component="div"
                              className="error-message-date"
                            />
                            <ErrorMessage
                              name="end_date"
                              component="div"
                              className="error-message-date"
                            />
                          </span>
                        </>
                      )}
                    </Field>
                  </div>
                </div>

                <div className="role_selection_BioData">
                  <label htmlFor="title" className="contract_field_title">
                    The guest
                  </label>

                  <div className="personNames">
                    <div className="name_input_field">
                      {screenWidth > 768 && (
                        <label htmlFor="name" className="bioData_label">
                          Name
                        </label>
                      )}

                      <div>
                        <Field
                          type="text"
                          name="name"
                          placeholder={screenWidth <= 768 ? "Name" : ""}
                          className="contract_input_field"
                        />
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="error-message"
                        />
                      </div>
                    </div>
                    <div className="surname_input_field">
                      {screenWidth > 768 && (
                        <label htmlFor="surname" className="bioData_label">
                          Surname
                        </label>
                      )}

                      <div>
                        <Field
                          type="text"
                          name="surname"
                          placeholder={screenWidth <= 768 ? "Surname" : ""}
                          className="contract_input_field"
                        />
                        <ErrorMessage
                          name="surname"
                          component="div"
                          className="error-message"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="phone_number_field">
                    {screenWidth > 768 && (
                      <label htmlFor="phone" className="bioData_label">
                        Phone
                      </label>
                    )}

                    <div>
                      <Field
                        type="number"
                        name="phone"
                        placeholder="Phone number"
                        component={PhoneNumberInput}
                        className="contract_input_field"
                      />
                    </div>
                  </div>

                  <div className="email_input_field">
                    {screenWidth > 768 && (
                      <label htmlFor="email" className="bioData_label">
                        Email
                      </label>
                    )}

                    <div>
                      <Field
                        type="email"
                        name="email"
                        placeholder={
                          screenWidth > 768 ? "Add your email" : "E-mail"
                        }
                        className={`contract_input_field_email`}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>
                </div>

                <div className="cleanliness_field_wrapper">
                  <label htmlFor="cleanliness" className="contract_field_title">
                    {" "}
                    What are your cleaning expectations?
                  </label>
                  <div className="cleanliness_field">
                    <span
                      className={`clean ${
                        formikProps.values.cleanliness === "Clean"
                          ? " selected"
                          : ""
                      }`}
                      onClick={() =>
                        formikProps.setFieldValue("cleanliness", "Clean")
                      }
                    >
                      Clean
                    </span>
                    <span
                      className={`veryClean ${
                        formikProps.values.cleanliness === "Very clean"
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        formikProps.setFieldValue("cleanliness", "Very clean")
                      }
                    >
                      Very clean
                    </span>
                    <span
                      className={`sugerMegaClean ${
                        formikProps.values.cleanliness === "Sugar Mega clean"
                          ? "selected"
                          : ""
                      }`}
                      onClick={() =>
                        formikProps.setFieldValue(
                          "cleanliness",
                          "Sugar Mega clean"
                        )
                      }
                    >
                      Sugar Mega clean!
                    </span>
                  </div>
                  <span style={{ marginTop: "-22px" }}>
                    <ErrorMessage
                      name="cleanliness"
                      component="div"
                      className="error-message"
                    />
                  </span>
                </div>

                <div className="role_of_house">
                  <label
                    htmlFor="contract_checkbox_title"
                    className="contract_field_title"
                  >
                    More questions to understand your lifestyle:
                  </label>
                  <div className="role_of_house_checkbox">
                    <div className="checkbox_wrapper">
                      <label htmlFor="petsAllowed" id="checkbox_label">
                        Are you traveling with a pet?
                      </label>
                      <div className="checkbox">
                        <span
                          className={`yes ${
                            formikProps.values.rules.petsAllowed === true
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            formikProps.setFieldValue("rules.petsAllowed", true)
                          }
                        >
                          Yes
                        </span>
                        <span
                          className={`no ${
                            formikProps.values.rules.petsAllowed === false
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            formikProps.setFieldValue(
                              "rules.petsAllowed",
                              false
                            )
                          }
                        >
                          No
                        </span>
                      </div>
                    </div>

                    <div className="checkbox_wrapper">
                      <label htmlFor="guestsAllowed" id="checkbox_label">
                        Do you want to invite people over in the fat/house?
                      </label>
                      <div className="checkbox">
                        <span
                          className={`yes ${
                            formikProps.values.rules.sharedSpace === true
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            formikProps.setFieldValue("rules.sharedSpace", true)
                          }
                        >
                          Yes
                        </span>
                        <span
                          className={`no ${
                            formikProps.values.rules.sharedSpace === false
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            formikProps.setFieldValue(
                              "rules.sharedSpace",
                              false
                            )
                          }
                        >
                          No
                        </span>
                      </div>
                    </div>

                    <div className="checkbox_wrapper">
                      <label htmlFor="smokingAllowed" id="checkbox_label">
                        Are you smoking?
                      </label>
                      <div className="checkbox">
                        <span
                          className={`yes ${
                            formikProps.values.rules.smokingAllowed === true
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            formikProps.setFieldValue(
                              "rules.smokingAllowed",
                              true
                            )
                          }
                        >
                          Yes
                        </span>
                        <span
                          className={`no ${
                            formikProps.values.rules.smokingAllowed === false
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            formikProps.setFieldValue(
                              "rules.smokingAllowed",
                              false
                            )
                          }
                        >
                          No
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="trash_expectation_wrapper" style={{paddingTop:'20px'}}>
                  <label
                    htmlFor="expectations"
                    className="contract_field_title"
                  >
                    Are you planning on putting the trash out before leaving?
                  </label>
                  <div className="trashExpectation_checkbox">
                    <span>
                      <Field
                        type="checkbox"
                        name="expectations.trash"
                        id="trash-yes"
                        value="true"
                        checked={formikProps.values.expectations.trash === true}
                        onChange={() => {
                          formikProps.setFieldValue("expectations.trash", true);
                          formikProps.setFieldValue(
                            "expectations.guests",
                            false
                          );
                        }}
                      />
                      <label
                        htmlFor="trash-yes"
                        className="contract_mode_checkbox_label"
                      >
                        Yes{" "}
                      </label>
                    </span>
                    <span>
                      <Field
                        type="checkbox"
                        name="expectations.trash"
                        id="trash-no"
                        value="false"
                        checked={
                          formikProps.values.expectations.trash === false
                        }
                        onChange={() => {
                          formikProps.setFieldValue(
                            "expectations.trash",
                            false
                          );
                          formikProps.setFieldValue(
                            "expectations.guests",
                            true
                          );
                        }}
                      />
                      <label
                        htmlFor="trash-no"
                        className="contract_mode_checkbox_label"
                      >
                        No
                      </label>
                    </span>
                  </div>
                </div>

                <div className="rating_cleaning_standard">
                  <label htmlFor="title" className="contract_field_title">
                     We all have different cleaning standard. How would you rate yours
                  
                  </label>
                </div>
                <div className="slider_container">
                  <Field name="slider_score">
                    {({ field, form }) => (
                      <>
                        <div className="slider_labels">
                          <span>I left in a rush</span>
                          <span>Hotel standards</span>
                        </div>
                        <div className="slider_wrapper">
                          <input
                            type="range"
                            name="slider_score"
                            min="1"
                            max="100"
                            step="1"
                            value={field.value}
                            onChange={(e) => {
                              form.setFieldValue(
                                "slider_score",
                                parseInt(e.target.value)
                              );
                            }}
                            className="slider"
                            style={{
                              background: `linear-gradient(to right, #75BA6D ${
                                (field.value / 100) * 100
                              }%, #000000 ${(field.value / 100) * 100}%)`,
                            }}
                          />
                        </div>
                      </>
                    )}
                  </Field>
                </div>
                <div className="submit_formBtn">
                  <button type="submit" className="submitBtn">
                    {isLoading ? (
                      <>
                        submitting &nbsp;{" "}
                        <i className="fa fa-spinner fa-spin"></i>
                      </>
                    ) : (
                      "Submit Answer"
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default VisitorContractDetails;
